import { useEffect, useState } from "react";
import { updateInterpretationData } from "../../api/interpretation";
import { InterpretationDataFromAPI } from "../../api/types";
import { useInterpretationData } from "../../hooks/useInterpretationData";
import useTestId from "../../hooks/useTestId";
import { InterpretationLevel } from "../../routes/test/interpretation";
import Error from "../Error";
import Loader from "../AppLoader";
import RefreshIcon from "../icons/RefreshIcon";
import { useInterpretationContext } from "../../context/InterpretationContext";
import SeveritySelector from "./SeveritySelector";
import { useAuth } from "../../context/AuthContext";
import useFeatureFlags from "../../hooks/useFeatureFlags";
import InterpretationDropdownInput, {
    OptionGroup,
} from "./InterpretationDropdownInput";
import RadioButton from "../RadioButton";

export default function InterpretationDropdowns({
    triggerRefetch,
    interpretationLevel,
    setInterpretationLevel,
}: {
    triggerRefetch: () => void;
    interpretationLevel: InterpretationLevel;
    setInterpretationLevel: (interpretationLevel: InterpretationLevel) => void;
}) {
    const { isAllowed } = useAuth();
    const { featureFlags } = useFeatureFlags();

    const [isRefreshed, setIsRefreshed] = useState(false);

    const testId = useTestId();
    const { value, loading, error, refresh, refetch } = useInterpretationData();
    const {
        data: interpretationData,
        initialState,
        setData: setInterpretationData,
        setInitialState,
    } = useInterpretationContext();

    useEffect(() => {
        if (value && !initialState) {
            setInitialState({
                limiting_system: value?.limiting_system || undefined,
                detailed_interpretation:
                    value?.detailed_interpretation || undefined,
                severity: value?.severity || undefined,
            });
        }
    }, [value, initialState]);

    useEffect(() => {
        if (isRefreshed && value) {
            setInterpretationData({
                limiting_system: value?.limiting_system || undefined,
                detailed_interpretation:
                    value?.detailed_interpretation || undefined,
            });
            setIsRefreshed(false);
        }
    }, [value]);

    if (!loading && (error || !value)) {
        return <Error />;
    }

    const handleRefresh = async () => {
        await refresh();
        refetch();
        triggerRefetch();
        setIsRefreshed(true);
    };

    const handleSelect = async (
        data: Partial<
            Pick<
                InterpretationDataFromAPI,
                "limiting_system" | "detailed_interpretation" | "severity"
            >
        >
    ) => {
        const updatedData = Object.fromEntries(
            Object.entries(data).map(([key, value]) => [
                key,
                value || undefined,
            ])
        );
        await updateInterpretationData({
            testId,
            data: updatedData,
        });
        refetch();
        setInterpretationData({
            ...interpretationData,
            ...updatedData,
        });
    };

    const selectedLimitingSystemItem = value?.other_limiting_systems.find(
        (item) => item.label === value.limiting_system
    );
    const selectedDetailedInterpretationItem =
        value?.other_detailed_interpretations.find(
            (item) => item.label === value.detailed_interpretation
        );

    const isLimitingSystemSelected =
        value?.limiting_system && !selectedLimitingSystemItem?.is_none;

    const isSpecificDetailedInterpretationSelected = Boolean(
        value?.detailed_interpretation &&
            !selectedDetailedInterpretationItem?.is_disable_none_detailed
    );

    const detailedInterpretationOptionsByGroup: Array<OptionGroup> =
        value?.other_detailed_interpretations.reduce<Array<OptionGroup>>(
            (acc, curr) => {
                const group = acc.find((g) => g.title === curr.title);
                if (group) {
                    group.options.push(curr.label);
                } else {
                    acc.push({ title: curr.title, options: [curr.label] });
                }
                return acc;
            },
            []
        ) || [];

    return (
        <div className="bg-blue-dark w-full shadow-[0px_2px_8px_0px_#366CD640] rounded-md flex flex-col items-center justify-center px-5 xl:px-10 mb-4 py-2 xl:py-4">
            {loading ? (
                <Loader />
            ) : !value ? (
                <Error />
            ) : (
                <div className="self-stretch flex 2xl:gap-9">
                    <div className="grow w-full flex flex-col gap-1.5 xl:gap-3 px-4">
                        <div className="w-full flex gap-3 justify-between">
                            <div
                                className={`flex items-center gap-3 xl:gap-6 grow`}
                            >
                                <RadioButton
                                    checked={
                                        interpretationLevel ===
                                        InterpretationLevel.Primary
                                    }
                                    onChange={() =>
                                        setInterpretationLevel(
                                            InterpretationLevel.Primary
                                        )
                                    }
                                    white
                                />
                                <InterpretationDropdownInput
                                    isDisabled={!isAllowed("System edit")}
                                    value={value.limiting_system}
                                    options={value?.other_limiting_systems.map(
                                        (i) => i.label
                                    )}
                                    size="lg"
                                    label="Limiting physiological system"
                                    previuosValue={
                                        value.previous_limiting_system
                                    }
                                    onSelect={(val) =>
                                        handleSelect({
                                            limiting_system: val,
                                        })
                                    }
                                    getIsOptionDisabled={(option) => {
                                        return (
                                            isSpecificDetailedInterpretationSelected &&
                                            value?.other_limiting_systems
                                                .filter((i) => i.is_none)
                                                .map((i) => i.label)
                                                .includes(option)
                                        );
                                    }}
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-3 xl:gap-6">
                            <RadioButton
                                checked={
                                    interpretationLevel ===
                                    InterpretationLevel.Secondary
                                }
                                onChange={() =>
                                    setInterpretationLevel(
                                        InterpretationLevel.Secondary
                                    )
                                }
                                white
                            />
                            <InterpretationDropdownInput
                                isDisabled={!isAllowed("Interpretation edit")}
                                value={value.detailed_interpretation}
                                options={detailedInterpretationOptionsByGroup}
                                size="md"
                                label="Suggested detailed interpretation"
                                previuosValue={
                                    value.previous_detailed_interpretation
                                }
                                onSelect={(val) =>
                                    handleSelect({
                                        detailed_interpretation:
                                            val || undefined,
                                    })
                                }
                                getIsOptionDisabled={(option) =>
                                    !isLimitingSystemSelected &&
                                    !value.other_detailed_interpretations
                                        .filter(
                                            (i) => i.is_disable_none_detailed
                                        )
                                        .map((i) => i.label)
                                        .includes(option)
                                }
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 shrink-0">
                        <SeveritySelector
                            disabled={
                                !isLimitingSystemSelected ||
                                !isAllowed("Severity edit")
                            }
                            onSelect={async (severity) => {
                                await handleSelect({ severity: severity });
                            }}
                            value={value.severity}
                        />
                        {!featureFlags?.hide_interpretation_recalculate_button && (
                            <button
                                aria-label="Refresh"
                                onClick={handleRefresh}
                                className="size-8 text-base font-semibold border-white border-[1.5px] flex items-center justify-center rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={
                                    !isAllowed("Interpretation recalculate")
                                }
                            >
                                <RefreshIcon className="size-5 stroke-white" />
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
